/**
 * Reading the `afRecordSource` XML the Windows designer stores on reports and
 * on report parameters.
 *
 * A record source is a serialized .NET object, so the document is machine
 * generated and its shape is fixed. Only a handful of elements matter here, and
 * parsing them with a reader rather than `DOMParser` keeps this module usable
 * outside a browser.
 */

import type { ReportRecordSource, ReportRecordSourceColumn } from "./types.js";

const XML_ENTITIES: Record<string, string> = {
	amp: "&",
	lt: "<",
	gt: ">",
	quot: '"',
	apos: "'",
};

/** Resolves the XML entities the .NET serializer emits. */
export function decodeXml(value: string) {
	return value.replace(/&(#x?[0-9a-f]+|[a-z]+);/gi, (match, entity: string) => {
		if (entity.startsWith("#x") || entity.startsWith("#X")) {
			return String.fromCodePoint(Number.parseInt(entity.slice(2), 16));
		}

		if (entity.startsWith("#")) {
			return String.fromCodePoint(Number.parseInt(entity.slice(1), 10));
		}

		return XML_ENTITIES[entity.toLowerCase()] ?? match;
	});
}

/** Escapes a value for use as XML text or as an attribute value. */
export function encodeXml(value: string) {
	return value
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;")
		.replace(/'/g, "&apos;");
}

/**
 * The text content of the first `<name>` element, or `null` when the element is
 * missing or self-closing (`<FilterString />`, which the serializer writes for
 * an empty value).
 */
export function readElement(xml: string, name: string): string | null {
	let match = new RegExp(`<${name}(?:\\s[^>]*)?>([\\s\\S]*?)</${name}>`).exec(xml);

	if (!match) return null;

	let value = decodeXml(match[1]).trim();

	return value === "" ? null : value;
}

/** The text content of every `<name>` element, in document order. */
export function readElements(xml: string, name: string): string[] {
	let matches = xml.matchAll(new RegExp(`<${name}(?:\\s[^>]*)?>([\\s\\S]*?)</${name}>`, "g"));

	return [...matches].map((match) => decodeXml(match[1]).trim());
}

/**
 * The CLR types the designer writes on a select column. Anything else — and a
 * column with no `DataType` at all, which is what it writes for strings — is
 * treated as text.
 */
const COLUMN_TYPES: Record<string, ReportRecordSourceColumn["type"]> = {
	Byte: "number",
	Decimal: "number",
	Double: "number",
	Int16: "number",
	Int32: "number",
	Int64: "number",
	Single: "number",
	Boolean: "boolean",
	DateTime: "date",
};

/** The columns of a `<SelectColumns>` block, in document order. */
function readSelectColumns(xml: string): ReportRecordSourceColumn[] {
	let block = /<SelectColumns>([\s\S]*?)<\/SelectColumns>/.exec(xml)?.[1] ?? "";
	let columns: ReportRecordSourceColumn[] = [];

	for (let match of block.matchAll(/<afSelectColumn>([\s\S]*?)<\/afSelectColumn>/g)) {
		let name = readElement(match[1], "Name");
		if (!name) continue;

		columns.push({ name, type: COLUMN_TYPES[readElement(match[1], "DataType") ?? ""] ?? "string" });
	}

	return columns;
}

/**
 * Reads the parts of an `afRecordSource` a web client can act on: the view, the
 * selected columns and the design-time filter.
 *
 * Returns `null` for anything that is not a record source, so a missing or
 * empty `RecordSource` column simply means "no lookup".
 */
export function parseRecordSource(xml: string | null | undefined): ReportRecordSource | null {
	if (!xml || !xml.includes("<afRecordSource")) return null;

	let viewName = readElement(xml, "ViewName");
	if (!viewName) return null;

	let maxRecords = readElement(xml, "MaxRecords");

	return {
		viewName,
		columns: readSelectColumns(xml),
		whereClause: readElement(xml, "WhereClause"),
		filterString: readElement(xml, "FilterString"),
		// The designer writes 0 for "no limit"; the web data API spells that -1.
		maxRecords: maxRecords === null || maxRecords === "0" ? null : Number(maxRecords),
	};
}
